import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, FileCheck2, RefreshCw, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { livraisonsService } from "@/api/services";
import { EmptyState } from "@/components/common/EmptyState";
import { ErrorState } from "@/components/common/ErrorState";
import { LoadingState } from "@/components/common/LoadingState";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { useAuth } from "@/auth/AuthProvider";
import {
  creerClePreuve,
  estReferenceSharePointSecuritaire,
  peutValiderPreuve,
} from "@/features/livraisons/livraison";
import type { PreuveLivraison } from "@/types/domain";

const erreurMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Opération sur la preuve impossible.";

export default function LivraisonsPage() {
  const { hasAnyPermission } = useAuth();
  const peutRevoir = hasAnyPermission("deliveries.proof.review");
  const [preuves, setPreuves] = useState<PreuveLivraison[]>([]);
  const [selection, setSelection] = useState<PreuveLivraison | null>(null);
  const [statut, setStatut] = useState("");
  const [expeditionId, setExpeditionId] = useState("");
  const [motif, setMotif] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await livraisonsService.list(statut || undefined);
      setPreuves(data);
      if (selection) setSelection(data.find((item) => item.id === selection.id) ?? null);
    } catch (cause) { setError(erreurMessage(cause)); }
    finally { setLoading(false); }
  }, [statut, selection?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void charger(); }, [charger]);

  const compteurs = useMemo(() => ({
    recues: preuves.filter((item) => item.statut === "RECU").length,
    autoValidables: preuves.filter((item) =>
      item.resultat_analyse.decision_proposee === "LIVRAISON_VALIDEE"
      && !item.livraison_confirmee
    ).length,
    revoir: preuves.filter((item) => ["A_VALIDER", "AMBIGU", "NON_IDENTIFIE", "SIGNATURE_NON_CONFIRMEE", "DONNEES_INCOHERENTES", "DOCUMENT_ILLISIBLE"].includes(item.statut)).length,
    validees: preuves.filter((item) => item.statut === "LIVRAISON_VALIDEE").length,
  }), [preuves]);

  const appliquer = async (action: "ASSOCIER" | "VALIDER" | "REFUSER") => {
    if (!selection) return;
    setBusy(true); setError(null);
    try {
      let resultat: PreuveLivraison;
      if (action === "ASSOCIER") {
        if (!expeditionId.trim() || motif.trim().length < 3) throw new Error("Expédition et motif sont requis.");
        resultat = await livraisonsService.associer(selection.id, {
          idempotency_key: creerClePreuve(action, selection.id),
          expedition_id: expeditionId.trim(), motif: motif.trim(),
        });
      } else {
        if (action === "REFUSER" && motif.trim().length < 3) throw new Error("Un motif de refus est requis.");
        const payload = { idempotency_key: creerClePreuve(action, selection.id), motif: motif.trim() || null };
        resultat = action === "VALIDER"
          ? await livraisonsService.valider(selection.id, payload)
          : await livraisonsService.refuser(selection.id, payload);
      }
      setSelection(resultat); setMotif(""); setExpeditionId("");
      setPreuves((items) => items.map((item) => item.id === resultat.id ? resultat : item));
    } catch (cause) { setError(erreurMessage(cause)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="livraisons-page">
      <PageHeader
        title="Preuves de livraison"
        subtitle="Revoir les documents signés et confirmer LIVRAISON_VALIDEE sans modifier le stock ni la facturation."
        breadcrumb={["Logistique", "Preuves de livraison"]}
        actions={<button type="button" onClick={() => void charger()} disabled={loading || busy} className="ops-focus inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-sm"><RefreshCw className="h-4 w-4" />Actualiser</button>}
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[["Reçues", compteurs.recues], ["Auto-validables", compteurs.autoValidables], ["À revoir", compteurs.revoir], ["Validées", compteurs.validees]].map(([label, value]) => (
          <div key={String(label)} className="ops-panel p-4"><p className="ops-label">{label}</p><p className="mt-1 font-mono text-2xl font-semibold">{value}</p></div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="preuve-statut" className="ops-label">Statut</label>
        <select id="preuve-statut" value={statut} onChange={(event) => setStatut(event.target.value)} className="ops-focus h-10 rounded-sm border bg-background px-3">
          <option value="">Tous</option><option value="RECU">Reçues</option><option value="A_VALIDER">À valider</option>
          <option value="AMBIGU">Ambiguës</option><option value="LIVRAISON_VALIDEE">Validées</option><option value="REFUSEE">Refusées</option>
        </select>
      </div>

      {loading ? <LoadingState label="Chargement des preuves..." rows={5} /> : error && !selection ? (
        <ErrorState title="Preuves indisponibles" description={error} onRetry={() => void charger()} />
      ) : preuves.length === 0 ? (
        <EmptyState title="Aucune preuve" description="Aucun document ne correspond au filtre actuel." />
      ) : <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.9fr)]">
        <section className="ops-panel divide-y">
          {preuves.map((preuve) => <button key={preuve.id} type="button" onClick={() => { setSelection(preuve); setExpeditionId(preuve.expedition_id ?? ""); }} className="ops-focus w-full p-4 text-left hover:bg-muted/50">
            <div className="flex flex-wrap items-start justify-between gap-2"><div><p className="font-semibold">{preuve.nom_fichier}</p><p className="mt-1 text-xs font-mono text-muted-foreground">{preuve.id}</p></div><StatusBadge status={preuve.statut} /></div>
            <p className="mt-2 text-sm">{preuve.commande ? `${preuve.commande.numero_commande} · ${preuve.commande.client}` : "Expédition non identifiée"}</p>
            <p className="mt-1 text-xs text-muted-foreground">Confiance {preuve.niveau_confiance} · {preuve.score_confiance}/100 · reçu {new Date(preuve.recu_le).toLocaleString("fr-CA")}</p>
          </button>)}
        </section>

        {selection && <aside className="ops-panel space-y-5 p-5" data-testid="preuve-detail">
          <div className="flex items-start justify-between gap-3"><div><p className="ops-label">Document</p><h2 className="break-all font-semibold">{selection.nom_fichier}</h2></div><StatusBadge status={selection.statut} /></div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2"><div><dt className="ops-label">Confiance</dt><dd>{selection.niveau_confiance} · {selection.score_confiance}/100</dd></div><div><dt className="ops-label">Signature</dt><dd>{selection.document_signe}</dd></div><div><dt className="ops-label">Expédition</dt><dd className="font-mono break-all">{selection.expedition_id ?? "Non associée"}</dd></div><div><dt className="ops-label">Exception</dt><dd className="font-mono">{selection.exception_id ?? "Aucune"}</dd></div></dl>
          {selection.raisons.length > 0 && <div><p className="ops-label">Raisons explicables</p><ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{selection.raisons.map((raison) => <li key={raison}>{raison}</li>)}</ul></div>}
          <div className="flex flex-wrap gap-2">
            {estReferenceSharePointSecuritaire(selection.reference_sharepoint) && <a href={selection.reference_sharepoint!} target="_blank" rel="noopener noreferrer" className="ops-focus inline-flex items-center gap-2 rounded-sm border px-3 py-2 text-sm"><ExternalLink className="h-4 w-4" />Ouvrir le document</a>}
            {selection.commande_id && <Link to={`/commandes/${selection.commande_id}`} className="ops-focus rounded-sm border px-3 py-2 text-sm">Ouvrir la commande</Link>}
            {selection.expedition_id && <Link to="/expedition" className="ops-focus rounded-sm border px-3 py-2 text-sm">Ouvrir les expéditions</Link>}
          </div>
          {selection.resultat_analyse.mode_observation && <div className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-500/10"><ShieldCheck className="mr-2 inline h-4 w-4" />Mode observation : la décision automatique proposée n'a pas été exécutée.</div>}
          {peutRevoir && selection.statut !== "LIVRAISON_VALIDEE" && selection.statut !== "REFUSEE" && <div className="space-y-3 border-t pt-4">
            <label className="ops-label" htmlFor="preuve-expedition">Expédition confirmée</label><input id="preuve-expedition" value={expeditionId} onChange={(event) => setExpeditionId(event.target.value)} className="ops-focus h-10 w-full rounded-sm border bg-background px-3 font-mono text-sm" />
            <label className="ops-label" htmlFor="preuve-motif">Motif de revue</label><textarea id="preuve-motif" value={motif} onChange={(event) => setMotif(event.target.value)} className="ops-focus min-h-20 w-full rounded-sm border bg-background p-3 text-sm" />
            <div className="flex flex-wrap gap-2"><button type="button" disabled={busy} onClick={() => void appliquer("ASSOCIER")} className="ops-focus rounded-sm border px-3 py-2 text-sm">Associer</button>{peutValiderPreuve(selection.statut, selection.expedition_id) && <button type="button" disabled={busy} onClick={() => void appliquer("VALIDER")} className="ops-focus inline-flex items-center gap-2 rounded-sm bg-emerald-700 px-3 py-2 text-sm font-semibold text-white"><FileCheck2 className="h-4 w-4" />Confirmer la livraison</button>}<button type="button" disabled={busy} onClick={() => void appliquer("REFUSER")} className="ops-focus rounded-sm border border-destructive px-3 py-2 text-sm text-destructive">Refuser</button></div>
          </div>}
          {error && <p role="alert" className="rounded-sm border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">{error}</p>}
        </aside>}
      </div>}
    </div>
  );
}
